import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, showNotif, Typography, useRightSideBar } from '@p81-common/ui';
import {
  MspParentWizard,
  MspParentAssociationPayload
} from '../MspParentWizard';
import {
  MspParentSemiWizard,
  MspParentSemiPayload
} from '../MspParentSemiWizard';
import { ChildWorkspace } from '../MspParentWizard/parts/SelectChildrenStep';
import { CredentialsFormValues } from '../shared/CredentialsStep';
import { CompletionScreen } from '../shared/CompletionScreen';
import {
  ValidationResultScreen,
  buildValidationResultLabels
} from '../shared/ValidationResultScreen';
import { ModeSelectScreen, ModeOption } from '../shared/ModeSelectScreen';
import { createClasses } from '../../styles';
import { buildMigrationLogsUrl } from '../../helpers/buildMigrationLogsUrl';
import {
  useAssociationOutcome,
  nothingStarted
} from '../../helpers/useAssociationOutcome';
import { fetchMspParentChildren } from '../MspParentWizard/helpers/fetchMspParentChildren';
import { runMspParentMigration } from '../MspParentWizard/helpers/runMspParentMigration';
import { runCreateUnderParentAssociation } from '../../helpers/runCreateUnderParentAssociation';
import { useRegion } from 'contexts/RegionContext';
import AssociateTenants from '../../AssociateTenants';

type MspParentMode =
  | 'CREATE_UNDER_PARENT'
  | 'MIGRATE_PARENT_AND_CHILDREN'
  | 'ASSOCIATE_EXISTING';
type Stage =
  | 'select'
  // The parent itself is migrated, using the child create-under-parent inputs.
  | 'create-under-parent'
  | 'migrate-parent-and-children'
  | 'associate-existing'
  | 'msp-parent-semi-migration-started'
  | 'msp-parent-migration-started'
  | 'msp-parent-workspace-started'
  // Pre-flow validation skipped some or all of the selected children.
  | 'validation-result';

const PRESCREEN = 'tenantsAssociation.mspParentPreScreen';
const WIZARD = 'tenantsAssociation.mspParentWizard';
const MIGRATION_STARTED = `${WIZARD}.migrationStartedStep`;
// The semi-migration moves a single tenant, so the bulk "for each child
// selected" copy does not fit - it reuses the child flow's screen instead.
const SEMI_MIGRATION_STARTED =
  'tenantsAssociation.mspChildWizard.migrationStartedStep';
const PARENT_WORKSPACE = 'tenantsAssociation.mspParentWorkspace';

const initialCredentials: CredentialsFormValues = {
  parentUuid: '',
  uuidConfirmed: false,
  clientId: '',
  secretKey: '',
  adminEmail: ''
};

interface MspParentModeSelectProps {
  companyName: string;
  customerId: string;
  tenantId: string;
  updateDataFn(): void;
}

const MspParentModeSelect = ({
  companyName,
  customerId,
  tenantId,
  updateDataFn
}: MspParentModeSelectProps) => {
  const { t } = useTranslation('activity');
  const classes = createClasses();
  const { setOpenSecondary, clearComponentsArray } = useRightSideBar();
  // The migration runs in the region the operator is currently viewing, so the
  // logs land in that region's Datadog org.
  const { region } = useRegion();
  const [mode, setMode] = useState<MspParentMode | null>(null);
  const [stage, setStage] = useState<Stage>('select');
  // Wizard state lives here (not inside MspParentWizard) so credentials and
  // child selections survive a Previous → re-select cycle.
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([]);
  const [credentials, setCredentials] =
    useState<CredentialsFormValues>(initialCredentials);
  const [scheduledConfirmed, setScheduledConfirmed] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const {
    migrationBatchId,
    upfrontErrors,
    validationResult,
    clearUpfrontErrors,
    handleOutcome
  } = useAssociationOutcome(t);
  // Which wizard step is showing - drives the per-step page title.
  const [wizardStep, setWizardStep] = useState(0);
  // Children load lazily when the user enters the migrate flow. Loading and
  // data are kept in a single state object so the resolve handler triggers
  // exactly one re-render (React 17 does not batch state updates inside
  // promise callbacks, and a double render here causes @p81-common/ui's
  // ResizeObserver polyfill to emit "loop completed with undelivered
  // notifications").
  const [childrenState, setChildrenState] = useState<{
    loading: boolean;
    list: readonly ChildWorkspace[];
    loaded: boolean;
  }>({ loading: false, list: [], loaded: false });

  const closeSidebar = useCallback(() => {
    setOpenSecondary(false);
    clearComponentsArray();
  }, [setOpenSecondary, clearComponentsArray]);

  useEffect(() => {
    if (stage !== 'migrate-parent-and-children' || childrenState.loaded) {
      return;
    }
    let cancelled = false;
    setChildrenState(prev => ({ ...prev, loading: true }));
    fetchMspParentChildren(customerId).then(result => {
      if (cancelled) return;
      if (result.ok) {
        setChildrenState({ loading: false, list: result.data, loaded: true });
        return;
      }
      setChildrenState(prev => ({ ...prev, loading: false }));
      const message = result.message
        ? t(`${WIZARD}.LOAD_CHILDREN_FAILED`, { message: result.message })
        : t(`${WIZARD}.LOAD_CHILDREN_FAILED_FALLBACK`);
      showNotif(message, { alertProps: { severity: 'error' } });
      closeSidebar();
    });
    return () => {
      cancelled = true;
    };
  }, [stage, childrenState.loaded, customerId, t, closeSidebar]);

  const handleNext = useCallback(() => {
    setWizardStep(0);
    if (mode === 'CREATE_UNDER_PARENT') {
      setStage('create-under-parent');
      return;
    }
    if (mode === 'MIGRATE_PARENT_AND_CHILDREN') {
      setStage('migrate-parent-and-children');
      return;
    }
    if (mode === 'ASSOCIATE_EXISTING') {
      setStage('associate-existing');
    }
  }, [mode]);

  // Typed values survive a Previous → re-select cycle; the two confirmations do
  // not. Both wizards on this screen share `scheduledConfirmed`, so without the
  // reset an operator who ticked it on the bulk confirm step would land on the
  // semi-migration confirm step with the box already ticked and Associate live.
  const handleBackToSelect = useCallback(() => {
    setStage('select');
    clearUpfrontErrors();
    setCredentials(prev => ({ ...prev, uuidConfirmed: false }));
    setScheduledConfirmed(false);
  }, [clearUpfrontErrors]);

  // Leaving the confirm step drops the failures it was showing, so the operator
  // does not come back to a box citing the rule they just fixed.
  const handleStepChange = useCallback(
    (step: number) => {
      setWizardStep(step);
      clearUpfrontErrors();
    },
    [clearUpfrontErrors]
  );

  const handleFinishMigration = useCallback(
    async (payload: MspParentAssociationPayload) => {
      setIsMigrating(true);
      const outcome = await runMspParentMigration(payload);
      setIsMigrating(false);

      const { screen, batchStarted } = handleOutcome(outcome, WIZARD);
      if (screen === 'started') setStage('msp-parent-migration-started');
      if (screen === 'validation-result') setStage('validation-result');
      if (batchStarted) {
        setCredentials(initialCredentials);
        setSelectedChildIds([]);
        setScheduledConfirmed(false);
        setMode(null);
        updateDataFn();
      }
    },
    [handleOutcome, updateDataFn]
  );

  const handleFinishSemiMigration = useCallback(
    async (payload: MspParentSemiPayload) => {
      setIsMigrating(true);
      const outcome = await runCreateUnderParentAssociation({
        tenantId,
        credentials: payload.credentials
      });
      setIsMigrating(false);

      const { screen, batchStarted } = handleOutcome(outcome, WIZARD);
      if (screen === 'started') setStage('msp-parent-semi-migration-started');
      if (screen === 'validation-result') setStage('validation-result');
      if (batchStarted) {
        setCredentials(initialCredentials);
        setScheduledConfirmed(false);
        setMode(null);
        updateDataFn();
      }
    },
    [tenantId, handleOutcome, updateDataFn]
  );

  const handleWorkspaceAssociated = useCallback(() => {
    setStage('msp-parent-workspace-started');
  }, []);

  const options = useMemo<ModeOption<MspParentMode>[]>(
    () => [
      {
        value: 'CREATE_UNDER_PARENT',
        label: t(`${PRESCREEN}.OPTION_CREATE_LABEL`),
        description: t(`${PRESCREEN}.OPTION_CREATE_DESC`)
      },
      {
        value: 'MIGRATE_PARENT_AND_CHILDREN',
        label: t(`${PRESCREEN}.OPTION_MIGRATE_LABEL`),
        description: t(`${PRESCREEN}.OPTION_MIGRATE_DESC`)
      },
      {
        value: 'ASSOCIATE_EXISTING',
        label: t(`${PRESCREEN}.OPTION_ASSOCIATE_LABEL`),
        description: t(`${PRESCREEN}.OPTION_ASSOCIATE_DESC`)
      }
    ],
    [t]
  );

  // The bulk wizard swaps the page title per step, per the Figma spec:
  // select children → credentials → confirm.
  const titleKey = useMemo(() => {
    if (stage === 'validation-result') {
      return validationResult && nothingStarted(validationResult)
        ? 'activity:tenantsAssociation.TITLE_NOT_STARTED'
        : 'activity:tenantsAssociation.TITLE_IN_PROGRESS';
    }
    // Not "success": the batch runs in the background long after the 202, so
    // these stages mean the migration was accepted, not that it finished.
    if (
      stage === 'msp-parent-semi-migration-started' ||
      stage === 'msp-parent-migration-started' ||
      stage === 'msp-parent-workspace-started'
    ) {
      return 'activity:tenantsAssociation.TITLE_IN_PROGRESS';
    }
    if (stage === 'associate-existing') {
      return `activity:${PARENT_WORKSPACE}.TITLE`;
    }
    // Two steps, titled like the child flow they share their inputs with:
    // credentials keeps the base title, confirm swaps to the Confirm one.
    if (stage === 'create-under-parent') {
      return wizardStep === 1
        ? 'activity:tenantsAssociation.TITLE_CONFIRM'
        : 'activity:tenantsAssociation.TITLE';
    }
    if (stage === 'migrate-parent-and-children') {
      if (wizardStep === 1) {
        return 'activity:tenantsAssociation.TITLE_CREDENTIALS';
      }
      if (wizardStep === 2) {
        return 'activity:tenantsAssociation.TITLE_CONFIRM';
      }
      return 'activity:tenantsAssociation.TITLE_CHILDREN';
    }
    return 'activity:tenantsAssociation.TITLE';
  }, [stage, wizardStep, validationResult]);

  const renderStage = () => {
    if (stage === 'validation-result' && validationResult) {
      const { tenants, validCount, invalidCount } = validationResult;
      const allRejected = nothingStarted(validationResult);
      return (
        <ValidationResultScreen
          labels={buildValidationResultLabels(t, { allRejected })}
          tenants={tenants}
          validCount={validCount}
          invalidCount={invalidCount}
          batchId={migrationBatchId}
          // Nothing started when everything was rejected, so there are no logs
          // to open - the CTA closes the drawer instead.
          {...(allRejected
            ? { onAction: closeSidebar }
            : {
                actionHref: buildMigrationLogsUrl(region.code, migrationBatchId)
              })}
        />
      );
    }

    if (stage === 'associate-existing') {
      return (
        <>
          <Typography variant="subtitle1" className={classes.subtitle}>
            {t(`${PARENT_WORKSPACE}.PAGE_SUBTITLE`)}
          </Typography>
          <AssociateTenants
            companyName={companyName}
            tenantId={tenantId}
            updateDataFn={updateDataFn}
            onBack={handleBackToSelect}
            variant="parentWorkspace"
            onAssociated={handleWorkspaceAssociated}
          />
        </>
      );
    }

    if (stage === 'create-under-parent') {
      return (
        <MspParentSemiWizard
          upfrontErrors={upfrontErrors}
          credentials={credentials}
          onCredentialsChange={setCredentials}
          scheduledConfirmed={scheduledConfirmed}
          onScheduledConfirmedChange={setScheduledConfirmed}
          onBack={handleBackToSelect}
          onFinish={handleFinishSemiMigration}
          isFinishing={isMigrating}
          onStepChange={handleStepChange}
        />
      );
    }

    if (stage === 'msp-parent-semi-migration-started') {
      return (
        <CompletionScreen
          title={t(`${SEMI_MIGRATION_STARTED}.TITLE`)}
          body={t(`${SEMI_MIGRATION_STARTED}.BODY`, { companyName })}
          actionLabel={t(`${SEMI_MIGRATION_STARTED}.VIEW_LOGS`)}
          actionHref={buildMigrationLogsUrl(region.code, migrationBatchId)}
        />
      );
    }

    if (stage === 'migrate-parent-and-children') {
      return (
        <MspParentWizard
          upfrontErrors={upfrontErrors}
          childWorkspaces={childrenState.list}
          isLoadingChildren={childrenState.loading}
          selectedChildIds={selectedChildIds}
          onSelectedChildIdsChange={setSelectedChildIds}
          credentials={credentials}
          onCredentialsChange={setCredentials}
          scheduledConfirmed={scheduledConfirmed}
          onScheduledConfirmedChange={setScheduledConfirmed}
          onBack={handleBackToSelect}
          onFinish={handleFinishMigration}
          isFinishing={isMigrating}
          onStepChange={handleStepChange}
        />
      );
    }

    if (stage === 'msp-parent-migration-started') {
      // Per-tenant progress for the batch shows up only in the Datadog log
      // stream, so that is where the success screen sends the operator.
      return (
        <CompletionScreen
          title={t(`${MIGRATION_STARTED}.TITLE`)}
          body={t(`${MIGRATION_STARTED}.BODY`)}
          actionLabel={t(`${MIGRATION_STARTED}.VIEW_LOGS`)}
          actionHref={buildMigrationLogsUrl(region.code, migrationBatchId)}
        />
      );
    }

    if (stage === 'msp-parent-workspace-started') {
      return (
        <CompletionScreen
          title={t(`${PARENT_WORKSPACE}.successScreen.TITLE`)}
          body={t(`${PARENT_WORKSPACE}.successScreen.BODY`, { companyName })}
          actionLabel={t(`${PARENT_WORKSPACE}.successScreen.DONE`)}
          onAction={closeSidebar}
        />
      );
    }

    return (
      <ModeSelectScreen
        subtitle={t(`${PRESCREEN}.PAGE_SUBTITLE`)}
        sectionTitle={t(`${PRESCREEN}.SECTION_TITLE`)}
        options={options}
        value={mode}
        onChange={setMode}
        radioName="msp-parent-mode"
        nextLabel={t(`${PRESCREEN}.NEXT`)}
        cancelLabel={t('buttons.CANCEL')}
        onNext={handleNext}
        onCancel={closeSidebar}
      />
    );
  };

  return (
    <Box>
      <Typography variant="h2" className={classes.heading}>
        <Trans
          i18nKey={titleKey}
          values={{ companyName }}
          components={{ span: <span /> }}
        />
      </Typography>
      {renderStage()}
    </Box>
  );
};

export default MspParentModeSelect;
